/**
 * /guides/wedding-seating-chart-vs-seating-plan
 *
 * "Wedding Seating Chart vs Seating Plan: What's the Difference?" — the third
 * production guide, and the terminology question in the wedding-seating
 * cluster. The first guide (/guides/wedding-seating-plan) answers "who sits
 * where"; the second answers "how many fit at a table"; this one answers the
 * question a reader asks before either of those, when two words for the same
 * thing have made them think they are missing a document.
 *
 * Its argument is that the two terms usually describe the same guest-to-table
 * assignment, and that the distinctions actually worth learning are the ones
 * between that assignment and an escort card, a place card and a floor plan.
 * Every regional claim is hedged — "particularly common in", never "is called"
 * — because usage is not uniform and the reader's own venue is the authority.
 * Keep the hedges; an absolute rule here would be wrong in half the world.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * Four, all in the closing section, and nothing beyond them:
 *
 *   - "Agapaé's seating tools are available on the Heirloom plan."  (the note)
 *   - the seating experience works from the guests already managed in the
 *     wedding
 *   - confirmed reception attendees can be moved into table assignments
 *   - the resulting plan can be reviewed
 *
 * Everything else about the product is out of bounds, and the prohibitions the
 * two earlier guides carry apply here in full — no drag-and-drop, no visual
 * floor plan or canvas, no automatic table generation, no automatic seating,
 * reassignment or optimization. This guide adds a fifth prohibition of its own,
 * because its subject invites it: it discusses guest-facing seating displays,
 * printed charts, escort cards and place cards at length, and Agapaé generates
 * none of them. Nothing in this file may imply that it does.
 *
 * ── Two structural notes ──────────────────────────────────────────────────
 * The source article marks several lines bold. `BLOCK_TYPES` has no inline
 * emphasis, and teaching both renderers about one would be infrastructure work
 * this content change has no reason to do. Each bold line is therefore rendered
 * as what it actually is: a label introducing the list under it becomes a
 * `subheading` (an H3), and a bold sentence standing on its own becomes an
 * ordinary paragraph — which is already how both earlier guides set a
 * standalone question or an arrow line.
 *
 * The two ordered sequences are `steps`, so the numbering lives in the <ol> and
 * the source strings carry none — the same call the first guide wrote down. In
 * "When Should You Create Your Seating Plan?" that means the ordinal words
 * ("First:", "Then:", "Next:") are gone: the list already says them.
 *
 * ── One thing to leave alone ──────────────────────────────────────────────
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it. The escort-card example reads
 * "Maria and Nikos" for that reason, which is also how every other pair in the
 * article is written.
 */

const weddingSeatingChartVsSeatingPlanEn = {
  slug: "wedding-seating-chart-vs-seating-plan",

  title: "Wedding Seating Chart vs Seating Plan: What's the Difference?",
  metaTitle:
    "Wedding Seating Chart vs Seating Plan: What's the Difference? | Agapaé",
  metaDescription:
    "Wedding seating chart or seating plan? Learn what the terms mean, how they differ from place cards and floor plans, and which one you actually need.",

  eyebrow: "Wedding seating",

  hero: {
    src: "/assets/images/moments/moment-1.jpg",
    // Describes the photograph, not the keyword: a black and white close-up of
    // two hands loosely clasped, a lace gown at one edge and blurred water
    // behind. Nothing about seating is visible in it, and saying otherwise
    // would be a lie told to exactly the readers who cannot see it.
    alt: "A black and white close-up of a bride and groom's hands loosely clasped, her lace gown at one edge of the frame and still water blurred behind them",
    width: 6983,
    height: 3436,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `If you have started planning your wedding reception, you may have come across both "seating chart" and "seating plan" — and wondered whether they are two different things.`,
    `In most wedding-planning contexts, they are not.`,
    `Both terms generally describe the same underlying assignment: deciding which guests will sit at which tables at the reception. The terminology changes depending on the country, venue, planner or tool you are using.`,
    `The more important distinction is between a seating chart or seating plan and things such as escort cards, place cards and a floor plan. Those serve different purposes, even though they all belong to the same seating process.`,
    `Once you understand the difference, the terminology becomes much easier to navigate.`,
  ],

  sections: [
    {
      heading: "Is a Wedding Seating Chart the Same as a Seating Plan?",
      content: [
        `Usually, yes.`,
        `A wedding seating chart and a wedding seating plan both describe the allocation of guests to tables at the reception.`,
        `For example:`,
        {
          type: "list",
          items: [
            `Anna and Mark → Table 3`,
            `Sofia → Table 3`,
            `James and Elena → Table 7`,
          ],
        },
        `The underlying information is the same: each guest or guest group has been assigned to a particular table.`,
        `Sometimes the terms are also used for the guest-facing display placed at the reception entrance. In other cases, "seating plan" refers more broadly to the working arrangement used by the couple, planner or venue.`,
        `The exact wording is less important than understanding what the document or tool is asking you to provide.`,
      ],
    },
    {
      heading: "Why Are There Two Names for the Same Thing?",
      content: [
        `The difference is largely regional and reflects how wedding professionals and couples use the language in different markets.`,
        `"Seating chart" is particularly common in the United States and Canada.`,
        `"Seating plan" or "table plan" is particularly common in the United Kingdom and Ireland.`,
        `You may also encounter other variations, including "table plan," "seat plan" or "seating arrangement."`,
        `There is no need to treat these as completely different wedding-planning products.`,
        `If your venue asks you for a seating plan, give them the guest-to-table assignment they need. If your planning tool calls the same thing a seating chart, the underlying task has not changed.`,
        `When in doubt, use the terminology your venue, planner or caterer uses. That keeps communication simple when several people are working from the same information.`,
      ],
    },
    {
      heading: "What Does a Seating Chart Actually Show?",
      content: [
        `At its simplest, a seating chart answers one question:`,
        `Which table is this guest sitting at?`,
        `A typical seating chart might contain:`,
        {
          type: "list",
          items: [
            `guest names`,
            `table names or numbers`,
            `the relationship between each guest and their assigned table`,
          ],
        },
        `It may be arranged alphabetically by guest name or grouped by table.`,
        `For example, an alphabetical version might look like:`,
        {
          type: "list",
          items: [`Maria → Table 2`, `Nikos → Table 5`, `Sofia → Table 2`],
        },
        `A table-based version might instead show:`,
        { type: "subheading", text: "Table 2" },
        {
          type: "list",
          items: [`Maria`, `Sofia`, `Andreas`, `Elena`],
        },
        { type: "subheading", text: "Table 5" },
        {
          type: "list",
          items: [`Nikos`, `Christina`, `George`, `Anna`],
        },
        `Both communicate the same underlying assignments.`,
        `The best format depends on how you want guests to find their table and how the information will be used.`,
      ],
    },
    {
      heading: "Seating Chart vs Seating Plan: Is There Any Real Difference?",
      content: [
        `For most couples, there is no important functional difference.`,
        `The two terms can describe the same guest-to-table assignment.`,
        `What can differ is the presentation.`,
        `One display might list every guest alphabetically and show a table number next to each name. Another might group guests under Table 1, Table 2, Table 3 and so on.`,
        `Those are different ways of presenting the same seating information.`,
        `So if you are comparing templates or searching online and see "seating chart" in one place and "seating plan" in another, you generally do not need two separate documents.`,
        `Look at what the template actually contains rather than the name used for it.`,
      ],
    },
    {
      heading: "What Is an Escort Card?",
      content: [
        `An escort card is an individual card that tells a guest which table they have been assigned to.`,
        `For example:`,
        `Maria and Nikos`,
        `Table 5`,
        `Escort cards are normally displayed together near the entrance to the reception.`,
        `Functionally, they solve the same immediate problem as a guest-facing seating chart: helping guests find their assigned table.`,
        `The difference is the format.`,
        `A seating chart is usually one larger display.`,
        `Escort cards are a collection of individual cards.`,
        `You generally do not need both simply to tell guests which table they are sitting at. The choice is usually about presentation, space, guest flow and the style of the reception.`,
      ],
    },
    {
      heading: "What Is a Place Card?",
      content: [
        `A place card goes one step further.`,
        `Instead of telling a guest which table to go to, it tells them which specific seat is theirs.`,
        `For example:`,
        `Maria`,
        `Table 5 — Seat 3`,
        `This means there are two different levels of assignment:`,
        {
          type: "list",
          items: [
            `Assigned table: Maria sits at Table 5.`,
            `Assigned seat: Maria sits at a specific position at Table 5.`,
          ],
        },
        `Many weddings only assign guests to tables and allow them to choose their own seat once they arrive.`,
        `Specific seat assignments can make more sense for a formal reception where each place setting is individually assigned.`,
        `The important point is that a place card is not the same thing as a seating chart. It is a more specific layer of seating information.`,
      ],
    },
    {
      heading: "Seating Chart vs Floor Plan",
      content: [
        `A seating chart tells you who sits where.`,
        `A floor plan tells you where the tables are physically positioned in the room.`,
        `For example, a floor plan may show:`,
        {
          type: "list",
          items: [
            `Table 1 near the dance floor`,
            `Tables 2 and 3 near the bar`,
            `a head table at the front`,
            `space for the DJ`,
            `entrances and exits`,
          ],
        },
        `The seating chart then connects guests to those tables.`,
        `Think of it as two related layers:`,
        `Floor plan → where the tables are`,
        `Seating plan → who is assigned to each table`,
        `They work together, but they are not the same document.`,
      ],
    },
    {
      heading: "Do You Assign Tables or Specific Seats?",
      content: [
        `For many weddings, assigning guests to tables is enough.`,
        `You might decide:`,
        `"The Smith family sits at Table 4."`,
        `The guests can then choose their own chairs when they arrive.`,
        `Alternatively, you can assign individual seats:`,
        `"Maria sits at Table 4, seat 2."`,
        `Whether you need seat-level assignments depends on the style of your reception, the level of formality and how much control you want over the final arrangement.`,
        `Do not assume that a seating chart automatically means every individual chair has been assigned.`,
        `A table assignment can be a complete seating plan in its own right.`,
      ],
    },
    {
      heading: "Which One Do You Actually Need?",
      content: [
        `For most couples, the simplest setup is:`,
        {
          // The numbering lives in the <ol>, so the source strings carry none.
          type: "steps",
          items: [
            `Guest list — know who is actually attending.`,
            `Table assignments — decide which guests belong at each table.`,
            `Seating plan / seating chart — keep those assignments in one clear plan.`,
            `Guest-facing display — choose whether guests will find their table using a seating chart, table plan or escort cards.`,
            `Place cards, if needed — add individual seat assignments only if your reception calls for them.`,
          ],
        },
        `You do not necessarily need a separate document for every one of these steps.`,
        `The important thing is to have one reliable set of guest-to-table assignments before creating anything that guests or vendors will see.`,
      ],
    },
    {
      heading: "A Simple Example",
      content: [
        `Imagine you have 80 confirmed guests and eight reception tables.`,
        `Your working seating plan might look like this:`,
        { type: "subheading", text: "Table 1" },
        {
          type: "list",
          items: [
            `Couple A`,
            `Couple B`,
            `Guest C`,
            `Guest D`,
            `Guest E`,
            `Guest F`,
            `Guest G`,
            `Guest H`,
          ],
        },
        { type: "subheading", text: "Table 2" },
        {
          type: "list",
          items: [
            `Couple I`,
            `Couple J`,
            `Guest K`,
            `Guest L`,
            `Guest M`,
            `Guest N`,
            `Guest O`,
            `Guest P`,
          ],
        },
        `And so on.`,
        `From that same underlying assignment, you could create a guest-facing seating chart showing each person's table.`,
        `You could instead use escort cards.`,
        `If you want individually assigned seats, you would add that information separately.`,
        `The key is that the guest-to-table assignment is the source information. The display format is a separate decision.`,
      ],
    },
    {
      heading: "When Should You Create Your Seating Plan?",
      content: [
        `You can start thinking about seating before your RSVPs are complete, but the real assignment becomes much more reliable once you know who is actually attending.`,
        `A practical sequence is:`,
        {
          // Ordered, so the ordinal words the source used ("First:", "Then:",
          // "Next:") would be said twice. The <ol> says them.
          type: "steps",
          items: [
            `Build and maintain your guest list.`,
            `Collect and review RSVPs.`,
            `Work from the confirmed attendees when creating the seating plan.`,
            `Review the arrangement with your partner and, where appropriate, your planner or venue.`,
            `Prepare the guest-facing display or other seating materials.`,
          ],
        },
        `This matters because guest changes can affect several tables at once. A cancellation, added guest or change in party size can require you to revisit the arrangement.`,
        `Your seating plan should therefore be treated as a working plan until the final details are settled.`,
      ],
    },
    {
      heading: "From Guest List to Seating Plan",
      content: [
        `A seating chart does not exist in isolation.`,
        `It starts with your guest information.`,
        `A useful planning flow is:`,
        `Guest list → RSVP → confirmed attendees → tables → seating assignments → guest-facing seating information`,
        `This is why it is helpful to keep your seating information connected to the guest list rather than rebuilding names manually in multiple places.`,
        `If a guest changes status or needs to move to another table, you want the underlying assignment to remain clear and easy to review.`,
      ],
    },
    {
      heading: "From Seating Plan to the Wedding Day",
      content: [
        `Once your assignments are settled, the seating plan becomes useful to more than just your guests.`,
        `Depending on your reception, the final information may help you and your team coordinate:`,
        {
          type: "list",
          items: [
            `table setup`,
            `guest placement`,
            `meal service`,
            `special seating considerations`,
            `last-minute changes`,
          ],
        },
        `The guest-facing version may be as simple as a printed seating chart at the entrance.`,
        `The working version can contain more detail.`,
        `The important thing is that everyone who needs the information is working from the same final assignments.`,
      ],
    },
    {
      // The source article titles this section "Where This Fits in Agapaé",
      // which is the heading the `related` block below already renders as an
      // H2. Two H2s reading the same words is a defect, and both earlier guides
      // resolved it the same way: the closing prose section is named for what
      // it does, and "Where this fits in Agapaé" stays with the related block.
      heading: "From Terminology to a Real Seating Plan",
      content: [
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type the two earlier guides put theirs.
          type: "note",
          text: `Agapaé's seating tools are available on the Heirloom plan.`,
        },
        `The seating experience works from the guests already managed in the wedding, helping you move from confirmed reception attendees to table assignments and review the resulting plan.`,
        {
          type: "p",
          text: [
            `If you are looking for the practical process behind creating the arrangement, see `,
            {
              text: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
              href: "/guides/wedding-seating-plan",
            },
            `.`,
          ],
        },
        {
          type: "p",
          text: [
            `For the product-specific seating experience, see `,
            { text: "Wedding Seating", href: "/wedding-seating" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the two earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `Is a seating chart the same as a seating plan?`,
      a: `In most wedding-planning contexts, yes. Both terms generally refer to the assignment of guests to tables at the reception. The terminology varies by region and by the venue, planner or tool you are using.`,
    },
    {
      q: `What is the difference between a seating chart and a seating plan?`,
      a: `Usually, there is no important functional difference. The terms are commonly used for the same guest-to-table assignment, although the information may be presented alphabetically, by table or in another format.`,
    },
    {
      q: `Is a seating plan the same as a floor plan?`,
      a: `No. A seating plan identifies which guests are assigned to which tables. A floor plan shows the physical position of those tables and other elements within the reception space.`,
    },
    {
      q: `What is the difference between a seating chart and a place card?`,
      a: `A seating chart normally tells a guest which table they are assigned to. A place card can tell them the specific seat they should use at that table. Place cards are therefore a more detailed layer of seating information.`,
    },
    {
      q: `Do I need to assign specific seats at a wedding?`,
      a: `Not necessarily. Many weddings assign guests to tables but allow them to choose their own seat within that table. Specific seat assignments are optional and depend on the style and formality of the reception.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // Deliberately empty, matching both earlier guides. The companion guide is
    // already linked from the prose at the point where a reader actually needs
    // it, and the capacity guide is not this article's next step; a link under
    // this heading would be a link built for a crawler rather than for the
    // person reading.
    guides: [],

    product: [
      {
        href: "/wedding-seating",
        label: "Wedding seating",
        blurb:
          "See how seating works in Agapaé, from the confirmed reception guests already in your wedding to their table assignments. Seating tools are available on the Heirloom plan.",
      },
    ],
  },
};

export default weddingSeatingChartVsSeatingPlanEn;
